export const NODE_TYPES = {
  START: 'start',
  END: 'end',
  DECISION: 'decision',
  ROUTE: 'route',
  ACTION: 'action',
  AUTOMATION: 'automation',
  REFERENCE: 'reference',
};

export const BRANCHING_NODE_TYPES = [NODE_TYPES.DECISION, NODE_TYPES.ROUTE];

// Automation execution: auto_pilot runs without agent confirmation,
// manual requires the agent to confirm first.
export const AUTOMATION_MODES = {
  AUTO_PILOT: 'auto_pilot',
  MANUAL: 'manual',
};

// Shared by the add-node context menu and the node components: icon, accent
// color and a help-text i18n key describing what the node does.
export const NODE_META = {
  [NODE_TYPES.START]: {
    icon: 'i-lucide-play',
    accentClass: 'bg-n-teal-9/15 text-n-teal-9',
  },
  [NODE_TYPES.END]: {
    icon: 'i-lucide-flag',
    accentClass: 'bg-n-slate-9/15 text-n-slate-11',
  },
  [NODE_TYPES.DECISION]: {
    icon: 'i-lucide-git-branch',
    accentClass: 'bg-n-amber-9/15 text-n-amber-11',
  },
  [NODE_TYPES.ROUTE]: {
    icon: 'i-lucide-signpost',
    accentClass: 'bg-n-blue-9/15 text-n-blue-11',
  },
  [NODE_TYPES.ACTION]: {
    icon: 'i-lucide-list-checks',
    accentClass: 'bg-n-iris-9/15 text-n-iris-11',
  },
  [NODE_TYPES.AUTOMATION]: {
    icon: 'i-lucide-zap',
    accentClass: 'bg-n-ruby-9/15 text-n-ruby-11',
  },
  [NODE_TYPES.REFERENCE]: {
    icon: 'i-lucide-corner-down-right',
    accentClass: 'bg-n-teal-9/15 text-n-teal-11',
  },
};

export const createBranch = (label = '') => ({
  id: crypto.randomUUID(),
  label,
});

const defaultDataFor = type => {
  switch (type) {
    case NODE_TYPES.DECISION:
    case NODE_TYPES.ROUTE:
      return { label: '', branches: [createBranch(), createBranch()] };
    case NODE_TYPES.ACTION:
      return { label: '', items: [''] };
    case NODE_TYPES.AUTOMATION:
      return { label: '', automation: null, mode: AUTOMATION_MODES.MANUAL };
    case NODE_TYPES.REFERENCE:
      return { label: '', operating_procedure_id: null };
    default:
      return { label: '' };
  }
};

export const createNode = (type, position) => ({
  id: crypto.randomUUID(),
  type,
  position,
  data: defaultDataFor(type),
});

// Regenerates ids (node + branch handles) so the clone has no edges and no
// handle-id collisions with the original. node.data may be a reactive proxy,
// which structuredClone rejects, so clone through JSON.
export const cloneNode = node => {
  const data = JSON.parse(JSON.stringify(node.data));
  if (Array.isArray(data.branches)) {
    data.branches = data.branches.map(branch => ({
      ...branch,
      id: crypto.randomUUID(),
    }));
  }
  return {
    id: crypto.randomUUID(),
    type: node.type,
    position: { x: node.position.x + 40, y: node.position.y + 40 },
    data,
  };
};

export const createEdge = ({ source, sourceHandle = null, target }) => ({
  id: crypto.randomUUID(),
  source,
  sourceHandle,
  target,
});

// Each output (branch handle or single source handle) allows only one next
// step — connecting a new edge from an already-wired output replaces the old one.
export const removeEdgesForSourceHandle = (
  edges,
  source,
  sourceHandle = null
) =>
  edges.filter(
    e =>
      !(
        e.source === source &&
        (e.sourceHandle || null) === (sourceHandle || null)
      )
  );

// Drop edges bound to a removed branch handle.
export const removeEdgesForBranch = (edges, branchId) =>
  edges.filter(e => e.sourceHandle !== branchId);

export const removeEdgesForNode = (edges, nodeId) =>
  edges.filter(e => e.source !== nodeId && e.target !== nodeId);

export const serializeFlow = (nodes, edges, viewport) => ({
  nodes: nodes.map(({ id, type, position, data }) => ({
    id,
    type,
    position: { x: position.x, y: position.y },
    data,
  })),
  edges: edges.map(({ id, source, sourceHandle, target }) => ({
    id,
    source,
    sourceHandle: sourceHandle || null,
    target,
  })),
  viewport,
});

export const findStartNode = nodes =>
  nodes.find(node => node.type === NODE_TYPES.START);

// Returns { error, warnings } where values are i18n keys under SOPS.BUILDER.VALIDATION.
export const validateFlow = (nodes, edges) => {
  const startNodes = nodes.filter(node => node.type === NODE_TYPES.START);
  if (startNodes.length !== 1) {
    return { error: 'START_REQUIRED', warnings: [] };
  }
  if (
    nodes.some(
      node =>
        node.type === NODE_TYPES.REFERENCE && !node.data.operating_procedure_id
    )
  ) {
    return { error: 'REFERENCE_UNCONFIGURED', warnings: [] };
  }

  const warnings = [];
  if (
    nodes.some(
      node => node.type === NODE_TYPES.AUTOMATION && !node.data.automation
    )
  ) {
    warnings.push('AUTOMATION_UNCONFIGURED');
  }
  if (
    nodes.some(
      node =>
        BRANCHING_NODE_TYPES.includes(node.type) && !node.data.branches?.length
    )
  ) {
    warnings.push('EMPTY_BRANCHES');
  }
  if (
    nodes.some(
      node =>
        node.type !== NODE_TYPES.END &&
        !edges.some(edge => edge.source === node.id)
    )
  ) {
    warnings.push('DEAD_ENDS');
  }
  return { error: null, warnings };
};
